import type { ButtonHTMLAttributes, Ref } from 'react';
import { Link, type To } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { Hint } from '@/components/ui/hint';
import { cn } from '@/lib/cn';

// The ONE way back in the console. Born in «Operar la disrupción» (PO 2026-10-27:
// «unificame las formas de volver para atrás… una flecha linda para atrás a la derecha
// arriba siempre… sin texto»), then made the whole application's (PO 2026-10-29): a round
// outline button with the arrow alone, at the TOP RIGHT of the layer it closes — a panel
// back to the disruption, a declare step back to the one before, a detail screen back to
// its list. Its name is the accessible label and the console's hint on hover / focus.
// `BackButton` steps back inside a screen (a click); `BackLink` leads to another route.
// A ✕ stays a ✕: it leaves a screen, it is not a step back.

const BACK =
  'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-primary/25 bg-background text-primary transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50';

export function BackButton({
  label,
  testId,
  className,
  ref,
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> & {
  /** Where it goes back to — the accessible name and the hint. */
  label: string;
  testId?: string;
  /** Radix `asChild` (a dialog's Close) hands its ref and handlers to the button. */
  ref?: Ref<HTMLButtonElement>;
}) {
  return (
    <Hint body={label}>
      <button
        type="button"
        {...rest}
        ref={ref}
        aria-label={label}
        className={cn(BACK, className)}
        data-testid={testId}
      >
        <ArrowLeft aria-hidden className="h-5 w-5" />
      </button>
    </Hint>
  );
}

/** The same arrow as a link: a screen's way back to the route it came from (its list). */
export function BackLink({
  to,
  label,
  testId,
  className,
}: {
  to: To;
  /** Where it goes back to — the accessible name and the hint. */
  label: string;
  testId?: string;
  className?: string;
}) {
  return (
    <Hint body={label}>
      <Link to={to} aria-label={label} className={cn(BACK, className)} data-testid={testId}>
        <ArrowLeft aria-hidden className="h-5 w-5" />
      </Link>
    </Hint>
  );
}
